// Supervision (H7-07, D-008, lot L6-A) : filtres des routes /supervision et liste fermée des colonnes
// autorisées dans les réponses et les exports. Aucune colonne ne désigne une personne : seules des
// collectivités, des territoires et des agrégats transitent. Les colonnes financières (vue finances,
// montant des moyens transférés) n'existent que pour les institutions disposant du droit financier.
import { z } from 'zod';
import { CODES_COMPETENCES, type CodeCompetence } from './supervision-competences';
import {
  CODES_INDICATEURS_SUPERVISION,
  MASSES_PARTAGE,
  NORMALISATIONS,
  VUES_AGREGATS,
  type CodeIndicateurSupervision,
  type MassePartage,
  type VueAgregat,
} from './supervision-indicateurs';

export const INSTITUTIONS_SUPERVISION = ['ministere', 'autorite_financiere', 'prefecture', 'gouvernorat'] as const;
/** Institutions dont le périmètre territorial est obligatoire (D-027). */
export const INSTITUTIONS_A_PERIMETRE = ['prefecture', 'gouvernorat'] as const;

const IDENTITE_COLLECTIVITE = ['collectivite_id', 'code_court', 'nom', 'type', 'province_code'] as const;

export const COLONNES_SUPERVISION = {
  repertoire: [
    'collectivite_id',
    'code_court',
    'nom',
    'type',
    'province_code',
    'province_nom',
    'departement_code',
    'departement_nom',
    'statut_deploiement',
    'fictive',
    'population_declaree',
    'superficie_km2',
  ],
  deploiement: [
    'collectivite_id',
    'code_court',
    'nom',
    'type',
    'province_code',
    'departement_code',
    'statut_deploiement',
    'fictive',
    'population_declaree',
    'modules_actifs',
    'nombre_modules_actifs',
    'utilisateurs_actifs_30j',
    'demarches_publiees',
    'derniere_activite',
    'calcule_le',
  ],
  dossiers: ['collectivite_id', 'code_court', 'nom', 'province_code', 'mois', 'recus', 'clotures', 'delai_median_jours', 'en_retard', 'calcule_le'],
  // Lot L6-A : activité par collectivité (valeur nulle = indisponible, motif dans « indisponibles »).
  activite: [
    ...IDENTITE_COLLECTIVITE,
    'fictive',
    'demarches_publiees',
    'dossiers_en_ligne',
    'interventions_cloturees',
    'projets_en_cours',
    'deliberations_transmises',
    'anomalies_ouvertes',
    'indisponibles',
    'calcule_le',
  ],
  comparaison: [...IDENTITE_COLLECTIVITE, 'fictive', 'numerateur', 'denominateur', 'valeur', 'etat', 'motif'],
  finances: [...IDENTITE_COLLECTIVITE, 'fictive', 'masse', 'partage', 'provenance_partage', 'etat', 'montant', 'calcule_le'],
  competences: [
    'competence_id',
    'collectivite_id',
    'code_court',
    'nom',
    'province_code',
    'competence',
    'libelle_competence',
    'texte_reference',
    'date_texte',
    'date_effet',
    'statut',
    'observation',
    'moyens',
    'fictif',
    'provenance',
    'version',
    'modifie_le',
  ],
} as const;
export type VueSupervision = keyof typeof COLONNES_SUPERVISION;
export const VUES_SUPERVISION = Object.keys(COLONNES_SUPERVISION) as VueSupervision[];
/** Vues réservées au droit financier (D-273). */
export const VUES_FINANCIERES: readonly VueSupervision[] = ['finances'];
/** Colonne ajoutée à la vue des compétences avec le droit financier seulement. */
export const COLONNES_COMPETENCES_FINANCIERES = ['moyens_financiers_montant'] as const;

/** Motif de nom de colonne évoquant une personne : refusé dans toute réponse de supervision. */
export const MOTIF_COLONNE_NOMINATIVE =
  /(^|_)(compte|appartenance|identifiant|courriel|telephone|prenoms?|usager|agent|tiers|auteur|instructeur|session|adresse|beneficiaire|numero)(_|$)/;

const code = z.string().trim().regex(/^[A-Z0-9-]{1,10}$/, 'Code territorial invalide');
const mois = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mois AAAA-MM attendu');

export const schemaFiltreRepertoire = z.object({
  province: code.optional(),
  departement: code.optional(),
  type: z.enum(['commune', 'conseil_departemental']).optional(),
});

export const schemaFiltreDossiersSupervision = schemaFiltreRepertoire.extend({
  debut: z.iso.date('Date AAAA-MM-JJ attendue').optional(),
  fin: z.iso.date('Date AAAA-MM-JJ attendue').optional(),
});

const moisOrdonnes = (v: { mois_debut?: string; mois_fin?: string }) => !v.mois_debut || !v.mois_fin || v.mois_debut <= v.mois_fin;
const messageMois = { message: 'Le mois de début précède le mois de fin', path: ['mois_fin'] };

/** Période en mois civils (agrégats mensuels) ; défaut : les douze derniers mois. */
export const schemaFiltreActivite = schemaFiltreRepertoire.extend({ mois_debut: mois.optional(), mois_fin: mois.optional() }).refine(moisOrdonnes, messageMois);

export const schemaRequeteComparaison = schemaFiltreRepertoire
  .extend({
    indicateur: z.enum(CODES_INDICATEURS_SUPERVISION as [CodeIndicateurSupervision, ...CodeIndicateurSupervision[]]),
    normalisation: z.enum(NORMALISATIONS).default('aucune'),
    mois_debut: mois.optional(),
    mois_fin: mois.optional(),
  })
  .refine(moisOrdonnes, messageMois);

export const schemaFiltreCompetences = schemaFiltreRepertoire.extend({
  competence: z.enum(CODES_COMPETENCES as [CodeCompetence, ...CodeCompetence[]]).optional(),
  collectivite_id: z.uuid().optional(),
});

export const FORMATS_EXPORT_TABLEAU = ['csv', 'xlsx'] as const;

export const schemaExportSupervision = schemaFiltreDossiersSupervision
  .extend({
    vue: z.enum(VUES_SUPERVISION as [VueSupervision, ...VueSupervision[]]).default('deploiement'),
    format: z.enum(FORMATS_EXPORT_TABLEAU).default('csv'),
    indicateur: z.enum(CODES_INDICATEURS_SUPERVISION as [CodeIndicateurSupervision, ...CodeIndicateurSupervision[]]).optional(),
    normalisation: z.enum(NORMALISATIONS).default('aucune'),
    mois_debut: mois.optional(),
    mois_fin: mois.optional(),
    competence: z.enum(CODES_COMPETENCES as [CodeCompetence, ...CodeCompetence[]]).optional(),
  })
  .refine(moisOrdonnes, messageMois)
  .refine((v) => v.vue !== 'comparaison' || Boolean(v.indicateur), { message: 'Indicateur obligatoire pour exporter une comparaison', path: ['indicateur'] });

export const schemaRafraichissementAgregats = z.object({
  vue: z.enum(VUES_AGREGATS as unknown as [VueAgregat, ...VueAgregat[]]).optional(),
});

// --- Partage des agrégats financiers (D-272) --------------------------------------------------

const motif = z.string().trim().min(3, 'Motif obligatoire').max(500);

/** Choix de la collectivité, pour toutes les masses ou pour une masse ; « herite » rend la main au niveau supérieur. */
export const schemaPartageCollectivite = z
  .strictObject({
    niveau: z.enum(['collectivite', 'masse']),
    masse: z.enum(MASSES_PARTAGE as unknown as [MassePartage, ...MassePartage[]]).optional(),
    valeur: z.enum(['partage', 'non_partage', 'herite']),
    motif,
  })
  .refine((v) => (v.niveau === 'masse') === Boolean(v.masse), { message: 'Masse obligatoire au niveau masse, absente sinon', path: ['masse'] });
export type PartageCollectivite = z.infer<typeof schemaPartageCollectivite>;

/** Paramètre national, réservé à l'administration nationale de la plateforme. */
export const schemaPartageNational = z.strictObject({ valeur: z.enum(['partage', 'non_partage']), motif });

export interface EtatPartageMasse {
  masse: MassePartage;
  partage: boolean;
  provenance: string;
  niveaux: { national: string; collectivite: string | null; masse: string | null };
  modifie_le: string | null;
}

export interface PartageFinancierCollectivite {
  collectivite_id: string;
  statut_deploiement: string;
  masses: EtatPartageMasse[];
  historique: { niveau: string; masse: string | null; valeur: string; motif: string; cree_le: string }[];
}
